import { useQuery, useMutation } from "@tanstack/react-query"
import type { QueryFunctionContext, UseMutationResult } from "@tanstack/react-query"
import dayjs from "@/helpers/dayjs"
import type { FormValues } from "@/pages/pac/filings/types"
import type { PacFile } from "@/types/djangio"
import type { CreateFederalFileAndFormRequest } from "@/pages/pac/filings/components/create-filing-modal/types"
import type {
    FormAlabamaValues,
    FormArizonaValues,
    FormArkansasValues,
    FormColoradoValues,
    FormGeorgiaValues,
    FormIndianaValues,
    FormMichiganValues,
    FormNevadaValues,
    FormNorthCarolinaValues,
    FormPennsylvaniaValues,
    FormTennesseeValues,
    FormTexasValues,
} from "@/pages/pac/filings/state/types"
import { api } from "@/api"

const { ReportingJurisdictionType } = DjangIO.app.ledger.types
const { PacFileFormStatusType } = DjangIO.app.pac.types
const { LedgerSettings } = DjangIO.app.ledger.models
const { Organization } = DjangIO.app.userdata.models

export const pacFileKeys = {
    all: [{ _model: "pacfile" }] as const,
    detail: (id: string) => [{ ...pacFileKeys.all[0], id }] as const,
    latest: (jurisdictionKey: string, ledgerSettingsId: string, pacReportSuperType?: number) =>
        [
            { ...pacFileKeys.all[0], entity: "latest", jurisdictionKey, ledgerSettingsId, pacReportSuperType },
            "latest",
        ] as const,
}

const fetchPacFile = async ({
    queryKey,
}: QueryFunctionContext<ReturnType<(typeof pacFileKeys)["detail"]>>): Promise<PacFile> => {
    const [{ id }] = queryKey

    const response = await (DjangIO as any).app.pac.models.pac_file.PacFile.objects.get(id).GET()

    return response
}

const fetchLatestPacFileByJurisdiction = async ({
    queryKey: [{ ledgerSettingsId, jurisdictionKey, pacReportSuperType }],
}: QueryFunctionContext<ReturnType<(typeof pacFileKeys)["latest"]>>): Promise<PacFile | null> => {
    const jurisdictionId = DjangIO.app.ledger.types.ReportingJurisdictionType[jurisdictionKey].value

    const response = await api.get({
        model: DjangIO.app.pac.models.pac_file.PacFile,
        params: {
            archived: "false",
            ledger_settings_id: String(ledgerSettingsId),
            jurisdiction: String(jurisdictionId),
            order_by: "-end_date",
            limit: "1",
            ...(pacReportSuperType ? { pac_report_super_type_is: String(pacReportSuperType) } : {}),
        },
    })

    const parsedResponse = await response.json()

    return parsedResponse.objects.length === 0 ? null : parsedResponse.objects[0]
}

interface UseLatestPacFileProps {
    ledgerSettingsId: string
    jurisdictionKey: string
    pacReportSuperType?: number
}

export const useLatestPacFile = ({ ledgerSettingsId, jurisdictionKey, pacReportSuperType }: UseLatestPacFileProps) =>
    useQuery({
        queryKey: pacFileKeys.latest(jurisdictionKey, ledgerSettingsId, pacReportSuperType),
        queryFn: fetchLatestPacFileByJurisdiction,
        enabled: Boolean(ledgerSettingsId) && Boolean(jurisdictionKey),
        select: (file: any | null) => {
            if (file) return loadPacData(file, jurisdictionKey)
            return null
        },
    })

const queryAdditionalMetadata = (jurisdictionKey: string) => {
    switch (jurisdictionKey) {
        case "alabama":
            return (data: PacFile) => {
                const fridayDate = data.additional_metadata.al_friday_date
                    ? dayjs
                          .tz(data.additional_metadata.al_friday_date as string, "America/New_York")
                          .tz(dayjs.tz.guess())
                    : null

                // TODO: Bring options values coming from Report
                return {
                    item1BeginningBalance: data.additional_metadata?.al_item_01_beginning_balance,
                    item4bNonItemizedReceipts: data.additional_metadata?.al_item_4b_non_itemized_receipts,
                    description: data.additional_metadata?.al_description,
                    fields: [
                        data.additional_metadata.al_new_address ? "newAddress" : "",
                        data.additional_metadata.al_final_report ? "finalReport" : "",
                    ],
                    fridayDate: fridayDate,
                    item13TotalCampaignDebt: data.additional_metadata?.al_item_13_total_campaign_debt,
                    item7BeginningBalance: data.additional_metadata?.al_item_07_beginning_balance,
                }
            }
        case "arizona":
            return (data: PacFile) => {
                const electionCycleDate = data.additional_metadata.az_election_cycle_start_date
                    ? dayjs
                          .tz(data.additional_metadata.az_election_cycle_start_date as string, "America/New_York")
                          .tz(dayjs.tz.guess())
                    : null

                const reportDueStartDate = data.additional_metadata.az_report_due_start_date
                    ? dayjs
                          .tz(data.additional_metadata.az_report_due_start_date as string, "America/New_York")
                          .tz(dayjs.tz.guess())
                    : null

                const reportDueEndDate = data.additional_metadata.az_report_due_end_date
                    ? dayjs
                          .tz(data.additional_metadata.az_report_due_end_date as string, "America/New_York")
                          .tz(dayjs.tz.guess())
                    : null

                // TODO: Bring options values coming from Report
                return {
                    electionCycleStartDate: electionCycleDate,
                    electionCycle: data.additional_metadata.az_election_cycle,
                    reportDueStartDate: reportDueStartDate,
                    reportDueEndDate: reportDueEndDate,
                    checkFields: [
                        data.additional_metadata.az_standing_committee ? "standingCommittee" : "",
                        data.additional_metadata.az_termination_report ? "terminationReport" : "",
                        data.additional_metadata.az_new_address ? "newAddress" : "",
                        data.additional_metadata.az_new_treasurer_mailing_address ? "newTreasurerMailingAddress" : "",
                    ],
                    cashBalanceAtBeginning: data.additional_metadata.az_cash_balance_at_beginning,
                    totalSurplusPreviousCampaign: data.additional_metadata.az_total_surplus_previous_campaign,
                    debsAndObligationsOwedByCommittee:
                        data.additional_metadata.az_debs_and_obligations_owed_by_committee,
                }
            }
        case "arkansas":
            return (data: PacFile) => {
                return {
                    arkansasCheckFields: [
                        data.additional_metadata.ar_termination_report ? "terminationReport" : "",
                        data.additional_metadata.ar_new_address ? "newAddress" : "",
                    ],
                    item04BeginningOfPeriod: data.additional_metadata.ar_item_04_beginning_period,
                    contributionsAndExpeditures: data.additional_metadata.ar_contributions_and_expenditures,
                }
            }
        case "colorado":
            return (data: PacFile) => {
                return {
                    financialInstitutionName: data.additional_metadata?.co_financial_institution_name,
                    financialInstitutionAddress: data.additional_metadata?.co_financial_institution_address,
                    financialInstitutionCity: data.additional_metadata?.co_financial_institution_city,
                    financialInstitutionState: data.additional_metadata?.co_financial_institution_state,
                    financialInstitutionZipCode: data.additional_metadata?.co_financial_institution_zip_code,
                    coloradoCheckFields: [
                        data.additional_metadata.co_new_committee_address ? "newCommitteeAddress" : "",
                    ],
                    filingDescription: data.additional_metadata?.co_filing_description,
                    fundsBeginningPeriod: data.additional_metadata?.co_funds_beginning_period,
                }
            }
        case "georgia":
            return (data: PacFile) => {
                return {
                    item2EstimatedValue: data.additional_metadata?.ga_item_02_estimated_value,
                    item2CashAmount: data.additional_metadata?.ga_item_02_cash_amount,
                    item8EstimatedValue: data.additional_metadata?.ga_item_08_estimated_value,
                    item8CashAmount: data.additional_metadata?.ga_item_08_cash_amount,
                    description: data.additional_metadata?.ga_description,
                }
            }
        case "indiana":
            return (data: PacFile) => {
                return {
                    item13BeginningPeriod: data.additional_metadata?.in_item_13_beginning_period,
                    item14JanuaryCurrentYear: data.additional_metadata?.in_item_14_january_current_year,
                    item17aItemizedValue: data.additional_metadata?.in_item_17a_itemized_value,
                    indianaCheckFields: [
                        data.additional_metadata.in_new_address ? "newAddress" : "",
                        data.additional_metadata.in_new_committee ? "newCommittee" : "",
                    ],
                    filingDescription: data.additional_metadata?.in_filing_description,
                    otherReportDescription: data.additional_metadata?.in_other_report_description,
                }
            }
        case "michigan":
            return (data: PacFile) => {
                const electionDate = data.additional_metadata.mi_election_date
                    ? dayjs
                          .tz(data.additional_metadata.mi_election_date as string, "America/New_York")
                          .tz(dayjs.tz.guess())
                    : null

                const dateSigned = data.additional_metadata.mi_date_signed
                    ? dayjs
                          .tz(data.additional_metadata.mi_date_signed as string, "America/New_York")
                          .tz(dayjs.tz.guess())
                    : null

                // TODO: Bring options values coming from Report
                return {
                    beginningOfPeriod: data.additional_metadata.mi_beginning_of_period,
                    bookKeeperPrefix: data.additional_metadata.mi_book_keeper_prefix,
                    bookKeeperFirstName: data.additional_metadata.mi_book_keeper_first_name,
                    bookKeeperLastName: data.additional_metadata.mi_book_keeper_last_name,
                    bookKeeperSuffix: data.additional_metadata.mi_book_keeper_suffix,
                    owedByCommittee: data.additional_metadata.mi_owed_by_committee,
                    owedToCommittee: data.additional_metadata.mi_owed_to_committee,
                    electionDate: electionDate,
                    checkFields: [
                        data.additional_metadata.mi_report_michigan_transactions_only
                            ? "reportMichiganTransactionsOnly"
                            : "",
                        data.additional_metadata.mi_final_report ? "finalReport" : "",
                    ],
                    dateSigned: dateSigned,
                    signedById: data.additional_metadata.mi_signed_by,
                    treasurerPrefix: data.additional_metadata.mi_treasurer_prefix,
                    treasurerFirstName: data.additional_metadata.mi_treasurer_first_name,
                    treasurerLastName: data.additional_metadata.mi_treasurer_last_name,
                    treasurerSuffix: data.additional_metadata.mi_treasurer_suffix,
                    treasurersWorkPhone: data.additional_metadata.mi_treasurers_work_phone,
                }
            }
        case "nevada":
            return (data: PacFile) => {
                return {
                    nevadaOptions: [
                        data.additional_metadata.nv_report_transactions_only ? "nevadaReportTransactionsOnly" : "",
                    ],
                    nevadaReportTransactionsOnly: data.additional_metadata.nv_report_transactions_only,
                    totalMonetaryContributionsReceived: data.additional_metadata.nv_monetary_contributions_received,
                    loansGuaranteedBy3rdParty: data.additional_metadata.nv_loans_guaranteed_by_3rd_party,
                    loansForgiven: data.additional_metadata.nv_loans_forgiven,
                    totalValueOfInKindContributionsReceived:
                        data.additional_metadata.nv_value_inkind_contributions_received,
                    totalMonetaryExpensesPaid: data.additional_metadata.nv_monetary_expenses_paid_excess,
                    totalValueOfInKindExpenses: data.additional_metadata.nv_value_inkind_expenses_excess,
                    writtenCommitmentsForContributions:
                        data.additional_metadata.nv_written_commitments_for_contributions,
                }
            }
        case "north_carolina":
            return (data: PacFile) => {
                const dueDate = data.additional_metadata.nc_due_date
                    ? dayjs.tz(data.additional_metadata.nc_due_date as string, "America/New_York").tz(dayjs.tz.guess())
                    : null
                const dateOfElection = data.additional_metadata.nc_date_of_election
                    ? dayjs
                          .tz(data.additional_metadata.nc_date_of_election as string, "America/New_York")
                          .tz(dayjs.tz.guess())
                    : null

                return {
                    dueDate: dueDate,
                    dateOfElection: dateOfElection,
                    specialReportName: data.additional_metadata.nc_special_report_name,
                    electionCycleBeginning: data.additional_metadata.nc_election_cycle_beginning,
                    signature: data.additional_metadata.nc_signature,
                    beginningOfElectionCycle: data.additional_metadata.nc_beginning_of_election_cycle,
                    beginningOfReportingPeriod: data.additional_metadata.nc_beginning_of_reporting_period,
                    debtsOwedToTheCommittee: data.additional_metadata.nc_debts_owed_to_the_committee,
                    debtsOwedByTheCommittee: data.additional_metadata.nc_debts_owed_by_the_committee,
                    numberOfFundraisersThisReport: data.additional_metadata.nc_number_of_fundraisers_this_report,
                    item05AggregatedContributionsFromIndividuals:
                        data.additional_metadata.nc_item_05_aggregated_contributions_from_individuals,
                    item06ContributionsFromIndividuals:
                        data.additional_metadata.nc_item_06_contributions_from_individuals,
                    item28ContributionsToBeRefunded: data.additional_metadata.nc_item_28_contributions_to_be_refunded,
                    item11bContributionsFromNonProfitOrganizationsPeriod:
                        data.additional_metadata.nc_item_11_contributions_from_non_profit_organizations_period,
                    goodsAndServicesContributions: data.additional_metadata.nc_goods_and_services_contributions,
                    outstandingLoans: data.additional_metadata.nc_outstanding_loans,
                    forgivenLoansPeriod: data.additional_metadata.nc_forgiven_loans_period,
                    fortyEightHourNoticeReportsSumPeriod:
                        data.additional_metadata.nc_forty_eight_hour_notice_reports_sum_period,
                    item11bContributionsFromNonProfitOrganizationsElectionCycle:
                        data.additional_metadata.nc_item_11b_contributions_from_non_profit_organizations_election_cycle,
                    parentEntityAdministrativeSupport: data.additional_metadata.nc_parent_entity_administrative_support,
                    forgivenLoansElectionCycle: data.additional_metadata.nc_forgiven_loans_election_cycle,
                    fortyEightHourNoticeReportsSumElectionCycle:
                        data.additional_metadata.nc_forty_eight_hour_notice_reports_sum_election_cycle,
                }
            }
        case "pennsylvania":
            return (data: PacFile) => {
                const electionDate = data.additional_metadata.pa_election_date
                    ? dayjs
                          .tz(data.additional_metadata.pa_election_date as string, "America/New_York")
                          .tz(dayjs.tz.guess())
                    : null

                return {
                    electionDate: electionDate,
                    pennsylvaniaOptions: [
                        data.additional_metadata.pa_new_address ? "newAddress" : "",
                        data.additional_metadata.pa_new_treasurer_mailing_address ? "newTreasurerMailingAddress" : "",
                        data.additional_metadata.pa_termination_report ? "terminationReport" : "",
                        data.additional_metadata.pa_anonymous_contributions_to_unitemized
                            ? "anonymousContributionsToUnitemized"
                            : "",
                    ],
                    amountFromLastReport: data.additional_metadata.pa_amount_from_last_report,
                }
            }
        case "tennessee":
            return (data: PacFile) => {
                return {
                    balanceOnHandLastReport: data.additional_metadata.tn_balance_on_hand_last_report,
                    candidatesSupported: data.additional_metadata.tn_candidates_supported,
                    checkFields: [data.additional_metadata.tn_itemized_disbursements ? "itemizedDisbursements" : ""],
                    itemizedObligationsOutstanding: data.additional_metadata.tn_itemized_obligations_outstanding,
                    loansOutstanding: data.additional_metadata.tn_loans_outstanding,
                    unitemizedObligationsOutstanding: data.additional_metadata.tn_unitemized_obligations_outstanding,
                }
            }
        case "texas":
            return (data: PacFile) => {
                return {
                    otherElectionTypeToggle: data.additional_metadata.tx_other_election_type_toggle,
                    otherElectionName: data.additional_metadata.tx_other_election_name,
                    checkFieldsWhenIsFiled: [
                        data.additional_metadata.tx_original_report_filed_within_14_days
                            ? "originalReportFiledWithin14Days"
                            : "",
                        data.additional_metadata.tx_gpac_only ? "gpacOnly" : "",
                    ],
                    correctionExplanation: data.additional_metadata.tx_correction_explanation,
                    correctionNumber: data.additional_metadata.tx_correction_number,
                    description: data.additional_metadata.tx_description,
                    increasedLimits: [data.additional_metadata.tx_increased_limits ? "increasedLimits" : ""],
                    checkFields: [
                        data.additional_metadata.tx_report_transactions_for_all_states
                            ? "reportTransactionsForAllStates"
                            : "",
                        data.additional_metadata.tx_report_only_texas_state_and_local
                            ? "reportOnlyTexasStateAndLocal"
                            : "",
                        data.additional_metadata.tx_new_committee_address ? "newCommitteeAddress" : "",
                        data.additional_metadata.tx_new_treasurer_mailing_address ? "newTreasurerMailingAddress" : "",
                    ],
                    amountType: data.additional_metadata.tx_amount_type,
                    amount: data.additional_metadata.tx_amount,
                    cashOnHandBeginningOfPeriod: data.additional_metadata.tx_cash_on_hand_beginning_of_period,
                    totalUnitemizedLoans: data.additional_metadata.tx_total_unitemized_loans,
                    loansOwedByCommitteeAtEndOfPeriod:
                        data.additional_metadata.tx_loans_owed_by_committee_at_end_of_period,
                    emailAddress: data.additional_metadata.tx_email_address,
                    alternateEmail: data.additional_metadata.tx_alternate_email,
                    signature: data.additional_metadata.tx_signature,
                }
            }
        default:
            return () => {
                // Deliberately return nothing
            }
    }
}

export const usePacFile = (id: string, jurisdictionKey?: string) =>
    useQuery({
        queryKey: pacFileKeys.detail(id),
        queryFn: fetchPacFile,
        enabled: Boolean(id),
        select: (data) => {
            return loadPacData(data, jurisdictionKey)
        },
    })

const loadPacData = (data, jurisdictionKey: string) => {
    // Django's TIME_ZONE is set to America/New_York, so we'll use that to parse the date, but then convert to the user's timezone
    const startDate = dayjs.tz(data.start_date, "America/New_York").tz(dayjs.tz.guess())

    const endDate = dayjs.tz(data.end_date, "America/New_York").tz(dayjs.tz.guess())

    const submittedDate = data.submitted_timestamp
        ? dayjs.tz(data.submitted_timestamp, "America/New_York").tz(dayjs.tz.guess())
        : null

    const electionDateOverride = data.election_date_override
        ? dayjs.tz(data.election_date_override, "America/New_York").tz(dayjs.tz.guess())
        : null

    return {
        ...queryAdditionalMetadata(jurisdictionKey)(data),
        amendmentFormId: data.amendment_form_id,
        amendmentNumber: data.amendment_number,
        amendments: data.amendments,
        electionDateOverride: electionDateOverride ? electionDateOverride.toDate() : null,
        electionType: data.election ? data.election : null,
        electionTypeId: data.election ? String(data.election.id) : null,
        endDate: endDate.toDate(),
        filingDate: submittedDate ? submittedDate.toDate() : null,
        filingDisplayKey: data.filing_display_key,
        filingId: data.fec_id,
        formType: data.form_type,
        hasDownloadLinks: data.has_download_links,
        id: data.id,
        isReadOnly: !data.is_editable,
        isImportedFiling: data.is_imported_filing,
        isReportBeingGenerated: DjangIO.app.pac.types.PacFileFormStatusType.GENERATING.includes(data.status)
            ? true
            : false,
        isReportFiled: data.status === DjangIO.app.pac.types.PacFileFormStatusType.filed.value ? "Yes" : "No",
        // Temporarily define this until we can update all locations that use the Text version
        isReportFiledBoolean: data.status === DjangIO.app.pac.types.PacFileFormStatusType.filed.value,
        jurisdiction: data.jurisdiction,
        startDate: startDate.toDate(),
        status: data.status,
        typeOfReport: data.pac_report_type,
        typeOfReportId: data.pac_report_type && String(data.pac_report_type.id),
    }
}
const mutationAdditionalMetadata = (jurisdictionKey: string) => {
    const additionalMetadata = {
        arizona: (values: FormArizonaValues) => {
            const electionCycleStartDate = values.electionCycleStartDate
                ? dayjs(values.electionCycleStartDate).hour(0).minute(0)
                : null

            const reportDueStartDate = values.reportDueStartDate
                ? dayjs(values.reportDueStartDate).hour(0).minute(0)
                : null
            const reportDueEndDate = values.reportDueEndDate ? dayjs(values.reportDueEndDate).hour(0).minute(0) : null

            return {
                az_standing_committee: values.checkFields?.includes("standingCommittee"),
                az_termination_report: values.checkFields?.includes("terminationReport"),
                az_new_address: values.checkFields?.includes("newAddress"),
                az_new_treasurer_mailing_address: values.checkFields?.includes("newTreasurerMailingAddress"),
                az_election_cycle_start_date: electionCycleStartDate,
                az_election_cycle: values.electionCycle,
                az_report_due_start_date: reportDueStartDate,
                az_report_due_end_date: reportDueEndDate,
                az_cash_balance_at_beginning: values.cashBalanceAtBeginning,
                az_total_surplus_previous_campaign: values.totalSurplusPreviousCampaign,
                az_debs_and_obligations_owed_by_committee: values.debsAndObligationsOwedByCommittee,
            }
        },
        alabama: (values: FormAlabamaValues) => {
            const fridayDate = values.fridayDate ? dayjs(values.fridayDate).hour(0).minute(0) : null

            return {
                al_item_01_beginning_balance: values.item1BeginningBalance,
                al_item_4b_non_itemized_receipts: values.item4bNonItemizedReceipts,
                al_description: values.description,
                al_final_report: values.fields?.includes("finalReport"),
                al_new_address: values.fields?.includes("newAddress"),
                al_item_07_beginning_balance: values.item7BeginningBalance,
                al_item_13_total_campaign_debt: values.item13TotalCampaignDebt,
                al_friday_date: fridayDate ? fridayDate.toISOString() : null,
            }
        },
        arkansas: (values: FormArkansasValues) => {
            return {
                ar_termination_report: values.arkansasCheckFields?.includes("terminationReport"),
                ar_new_address: values.arkansasCheckFields?.includes("newAddress"),
                ar_item_04_beginning_period: values.item04BeginningOfPeriod,
                ar_contributions_and_expenditures: values.contributionsAndExpenditures === "noActivity" ? 0 : 1,
            }
        },
        colorado: (values: FormColoradoValues) => {
            return {
                co_financial_institution_name: values.financialInstitutionName,
                co_financial_institution_address: values.financialInstitutionAddress,
                co_financial_institution_city: values.financialInstitutionCity,
                co_financial_institution_state: values.financialInstitutionState,
                co_financial_institution_zip_code: values.financialInstitutionZipCode,
                co_new_committee_address: values.coloradoCheckFields?.includes("newCommitteeAddress"),
                co_filing_description: values.filingDescription,
                co_funds_beginning_period: values.fundsBeginningPeriod,
            }
        },
        federal: (values) => {
            return {}
        },
        georgia: (values: FormGeorgiaValues) => {
            return {
                ga_item_02_estimated_value: values.item2EstimatedValue,
                ga_item_02_cash_amount: values.item2CashAmount,
                ga_item_08_estimated_value: values.item8EstimatedValue,
                ga_item_08_cash_amount: values.item8CashAmount,
                ga_description: values.description,
            }
        },
        indiana: (values: FormIndianaValues) => {
            return {
                in_new_address: values.indianaCheckFields?.includes("newAddress"),
                in_new_committee: values.indianaCheckFields?.includes("newCommittee"),
                in_filing_description: values.filingDescription,
                in_other_report_description: values.otherReportDescription,
                in_item_13_beginning_period: values.item13BeginningPeriod,
                in_item_14_january_current_year: values.item14JanuaryCurrentYear,
                in_item_17a_itemized_value: values.item17aItemizedValue,
            }
        },
        michigan: (values: FormMichiganValues) => {
            const electionDate = values.electionDate ? dayjs(values.electionDate).hour(0).minute(0) : null
            const dateSigned = values.dateSigned ? dayjs(values.dateSigned).hour(0).minute(0) : null

            return {
                mi_beginning_of_period: values.beginningOfPeriod,
                mi_book_keeper_prefix: values.bookKeeperPrefix,
                mi_book_keeper_first_name: values.bookKeeperFirstName,
                mi_book_keeper_last_name: values.bookKeeperLastName,
                mi_book_keeper_suffix: values.bookKeeperSuffix,
                mi_owed_by_committee: values.owedByCommittee,
                mi_owed_to_committee: values.owedToCommittee,
                mi_election_date: electionDate,
                mi_report_michigan_transactions_only: values.checkFields?.includes("reportMichiganTransactionsOnly"),
                mi_final_report: values.checkFields?.includes("finalReport"),
                mi_date_signed: dateSigned,
                mi_signed_by: values.signedById,
                mi_treasurer_prefix: values.treasurerPrefix,
                mi_treasurer_first_name: values.treasurerFirstName,
                mi_treasurer_last_name: values.treasurerLastName,
                mi_treasurer_suffix: values.treasurerSuffix,
                mi_treasurers_work_phone: values.treasurersWorkPhone,
            }
        },
        nevada: (values: FormNevadaValues) => {
            // we only pass this value on creating a new report, and it is not editable by the form
            const reportingCycleStart = dayjs(new Date()).hour(0).minute(0)

            return {
                nv_reporting_cycle_start: reportingCycleStart,
                nv_report_transactions_only: values.nevadaOptions?.includes("nevadaReportTransactionsOnly"),
                nv_monetary_contributions_received: values.totalMonetaryContributionsReceived,
                nv_loans_guaranteed_by_3rd_party: values.loansGuaranteedBy3rdParty,
                nv_loans_forgiven: values.loansForgiven,
                nv_value_inkind_contributions_received: values.totalValueOfInKindContributionsReceived,
                nv_value_inkind_expenses_excess: values.totalValueOfInKindExpenses,
                nv_monetary_expenses_paid_excess: values.totalMonetaryExpensesPaid,
                nv_written_commitments_for_contributions: values.writtenCommitmentsForContributions,
            }
        },
        north_carolina: (values: FormNorthCarolinaValues) => {
            const dueDate = values.dueDate ? dayjs(values.dueDate).hour(0).minute(0) : null
            const dateOfElection = values.dateOfElection ? dayjs(values.dateOfElection).hour(0).minute(0) : null

            return {
                nc_due_date: dueDate,
                nc_date_of_election: dateOfElection,
                nc_special_report_name: values.specialReportName,
                nc_election_cycle_beginning: values.electionCycleBeginning,
                nc_signature: values.signature,
                nc_beginning_of_election_cycle: values.beginningOfElectionCycle,
                nc_beginning_of_reporting_period: values.beginningOfReportingPeriod,
                nc_debts_owed_to_the_committee: values.debtsOwedToTheCommittee,
                nc_debts_owed_by_the_committee: values.debtsOwedByTheCommittee,
                nc_number_of_fundraisers_this_report: values.numberOfFundraisersThisReport,
                nc_item_05_aggregated_contributions_from_individuals:
                    values.item05AggregatedContributionsFromIndividuals,
                nc_item_06_contributions_from_individuals: values.item06ContributionsFromIndividuals,
                nc_item_28_contributions_to_be_refunded: values.item28ContributionsToBeRefunded,
                nc_item_11_contributions_from_non_profit_organizations_period:
                    values.item11bContributionsFromNonProfitOrganizationsPeriod,
                nc_goods_and_services_contributions: values.goodsAndServicesContributions,
                nc_outstanding_loans: values.outstandingLoans,
                nc_forgiven_loans_period: values.forgivenLoansPeriod,
                nc_forty_eight_hour_notice_reports_sum_period: values.fortyEightHourNoticeReportsSumPeriod,
                nc_item_11b_contributions_from_non_profit_organizations_election_cycle:
                    values.item11bContributionsFromNonProfitOrganizationsElectionCycle,
                nc_parent_entity_administrative_support: values.parentEntityAdministrativeSupport,
                nc_forgiven_loans_election_cycle: values.forgivenLoansElectionCycle,
                nc_forty_eight_hour_notice_reports_sum_election_cycle:
                    values.fortyEightHourNoticeReportsSumElectionCycle,
            }
        },
        pennsylvania: (values: FormPennsylvaniaValues) => {
            const electionDate = values.electionDate ? dayjs(values.electionDate).hour(0).minute(0) : null
            return {
                pa_election_date: electionDate,
                pa_new_address: values.pennsylvaniaOptions?.includes("newAddress"),
                pa_new_treasurer_mailing_address: values.pennsylvaniaOptions?.includes("newTreasurerMailingAddress"),
                pa_termination_report: values.pennsylvaniaOptions?.includes("terminationReport"),
                pa_anonymous_contributions_to_unitemized: values.pennsylvaniaOptions?.includes(
                    "anonymousContributionsToUnitemized",
                ),
                pa_amount_from_last_report: values.amountFromLastReport,
            }
        },
        tennessee: (values: FormTennesseeValues) => {
            return {
                tn_balance_on_hand_last_report: values.balanceOnHandLastReport,
                tn_candidates_supported: values.candidatesSupported,
                tn_itemized_disbursements: values.checkFields.includes("itemizedDisbursements"),
                tn_itemized_obligations_outstanding: values.itemizedObligationsOutstanding,
                tn_loans_outstanding: values.loansOutstanding,
                tn_unitemized_obligations_outstanding: values.unitemizedObligationsOutstanding,
            }
        },
        texas: (values: FormTexasValues) => {
            return {
                tx_other_election_type_toggle: values.otherElectionTypeToggle,
                tx_other_election_name: values.otherElectionName,
                tx_original_report_filed_within_14_days: values.checkFieldsWhenIsFiled?.includes(
                    "originalReportFiledWithin14Days",
                ),
                tx_gpac_only: values.checkFieldsWhenIsFiled?.includes("gpacOnly"),
                tx_correction_number: values.correctionNumber,
                tx_description: values.description,
                tx_report_transactions_for_all_states: values.checkFields?.includes("reportTransactionsForAllStates"),
                tx_report_only_texas_state_and_local: values.checkFields?.includes("reportOnlyTexasStateAndLocal"),
                tx_new_committee_address: values.checkFields?.includes("newCommitteeAddress"),
                tx_new_treasurer_mailing_address: values.checkFields?.includes("newTreasurerMailingAddress"),
                tx_increased_limits: values.increasedLimits?.includes("increasedLimits"),
                tx_amount_type: values.amountType,
                tx_amount: values.amount,
                tx_cash_on_hand_beginning_of_period: values.cashOnHandBeginningOfPeriod,
                tx_total_unitemized_loans: values.totalUnitemizedLoans,
                tx_loans_owed_by_committee_at_end_of_period: values.loansOwedByCommitteeAtEndOfPeriod,
                tx_email_address: values.emailAddress,
                tx_alternate_email: values.alternateEmail,
                tx_signature: values.signature,
            }
        },
    }

    return additionalMetadata[jurisdictionKey] || {}
}

export const useMutatePacFile = (ledgerSettingsId: string, jurisdictionKey: string, filingsId?: string) =>
    useMutation({
        mutationFn: (values: FormValues) => {
            const jurisdiction = DjangIO.app.ledger.types.ReportingJurisdictionType[jurisdictionKey]
            const ledgerSettings = DjangIO.app.ledger.models.LedgerSettings.resourceUriFromId(ledgerSettingsId)
            const organization = DjangIO.app.userdata.models.Organization.resourceUriFromId(Userdata.organization_id)
            const amendmentNumber = values.amendmentNumber
            const election = values.electionTypeId
                ? (DjangIO as any).app.pac.models.election.Election.resourceUriFromId(
                      parseInt(values.electionTypeId, 10),
                  )
                : null
            const reportType = (DjangIO as any).app.pac.models.pac_report_type.PACReportType.resourceUriFromId(
                parseInt(values.typeOfReportId, 10),
            )

            // assuming that dates are being saved as UTC
            const filingDate = values.filingDate ? dayjs(values.filingDate).hour(0).minute(0) : null
            const startDate = dayjs(values.startDate).hour(0).minute(0)
            const endDate = dayjs(values.endDate).hour(0).minute(0)
            const electionDateOverride = values.electionDateOverride
                ? dayjs(values.electionDateOverride).hour(0).minute(0)
                : null
            const isReportFiled = values.isReportFiled === "Yes"
            const status =
                values.status === DjangIO.app.pac.types.PacFileFormStatusType.queued_for_filing.value
                    ? values.status
                    : undefined

            const updateData: Partial<PacFile> = {
                additional_metadata: mutationAdditionalMetadata(jurisdictionKey)(values),
                amendment_number: amendmentNumber,
                election: election,
                fec_id: values.filingId || "",
                election_date_override: electionDateOverride ? electionDateOverride.toISOString() : null,
                submitted_timestamp: isReportFiled && filingDate ? filingDate.toISOString() : null,
                status:
                    status ||
                    (isReportFiled
                        ? DjangIO.app.pac.types.PacFileFormStatusType.filed.value
                        : DjangIO.app.pac.types.PacFileFormStatusType.not_filed.value),
            }

            const createData: Partial<PacFile> = {
                ...updateData,
                end_date: endDate.toISOString(),
                form_type: DjangIO.app.pac.types.PacFileFormType.state_form.value,
                ledger_settings: ledgerSettings,
                organization: organization,
                pac_report_type: reportType,
                jurisdiction: jurisdiction.value,
                start_date: startDate.toISOString(),
                status: DjangIO.app.pac.types.PacFileFormStatusType.not_filed.value,
            }

            // If the filingsId exists, we update an existing report
            if (filingsId) {
                return (DjangIO as any).app.pac.models.pac_file.PacFile.objects
                    .get(filingsId)
                    .update(updateData)
                    .PATCH()
            }

            // Otherwise, we create a new report
            return (DjangIO as any).app.pac.models.pac_file.PacFile.objects.create(createData).POST()
        },
    })

const createFederalPacFileAndForm = async ({
    ledgerSettingsId,
    formTypeValue,
    reportTypeValue,
    formPeriodTypeValue,
    coverageStartDate,
    coverageEndDate,
}: CreateFederalFileAndFormRequest): Promise<PacFile> => {
    const body = {
        ledger_settings: LedgerSettings.resourceUriFromId(ledgerSettingsId),
        organization: Organization.resourceUriFromId(Userdata.organization_id),
        form_type: formTypeValue,
        form_period_type: formPeriodTypeValue,
        region: ReportingJurisdictionType.federal.value,
        status: PacFileFormStatusType.in_progress.value,
        coverage_start_date: coverageStartDate ? dayjs(coverageStartDate).format("MM/DD/YYYY") : null,
        coverage_end_date: coverageEndDate ? dayjs(coverageEndDate).format("MM/DD/YYYY") : null,
        report_code: reportTypeValue,
        amendment_number: 0,
    }

    const response = await api.post(
        {
            model: DjangIO.app.pac.models.pac_file.PacFile,
            action: "create_pacfile_and_form",
            params: {
                decode_enums: "false",
                count: "false",
            },
        },
        body,
        { throwOnNotOk: false },
    )

    const parsedResponse = await response.json()

    if (!response.ok) throw parsedResponse

    return parsedResponse as PacFile
}

export const useCreateFederalPacFileAndForm = (): UseMutationResult<PacFile> =>
    useMutation({
        mutationFn: createFederalPacFileAndForm,
    })
